import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Actions,
  Container,
  SnowflakeWrapper,
  TileTable,
  Value,
  Visibility,
  CompanyName,
  CellLink,
  PELabel,
} from './styled';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';
import PNV from '@/components/PositiveNegativeValue';
import Observer from '@researchgate/react-intersection-observer';
import type { BestValuationRatio } from '@/utilities/getBestValuationRatio';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { ROUTE_STOCKS_MARKET_INDUSTRY } from '@/constants/routes';
import {
  ValuationRatio,
  GrowthTooltip,
} from '@/pages/Stocks/components/StockTable';
import Tooltip from '@/components/Tooltip';
import loadable from '@loadable/component';
import { Box, Text, useTheme } from '@simplywallst/ui-core';
import Media from 'react-media';
import { toLocaleUrl } from '@/pages/CompanyReport/utils/toLocaleUrl';
import { useLocale } from '@/hooks/useLocale';

const WatchlistAddRemoveCompanyToggle = loadable(
  () => import('@/components/WatchlistAddRemoveCompanyToggle'),
  { ssr: false }
);

const Snowflake = loadable(() => import('@/components/Snowflake/Snowflake'));
export interface Company {
  canonicalUrl: string;
  companyId: string;
  shortDescription: string;
  mainThumb: string;
  shortName: string;
  snowflakePoints: number[];
  tickerSymbol: string;
  uniqueSymbol: string;
  return1D: number | null;
  return1DFormatted: string;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
  marketCap: number | null;
  marketCapFormatted: string;
  sharePrice: number | null;
  sharePriceFormatted: string;
  bestValuationRatio: BestValuationRatio;
  peFormatted: string;
  pbFormatted: string;
  psFormatted: string;
  analystCount: number | null;
  growthEstimate: number | null;
  growthEstimateFormatted: string;
  earningsGrowth: number | null;
  earningsGrowthFormatted: string;
  salesGrowth: number | null;
  salesGrowthFormatted: string;
  analystsPriceTarget: number | null;
  analystsPriceTargetFormatted: string;
  industrySlug: string;
  industryLabel: string;
}

export interface Props {
  company: Company;
  section: string;
  industry: string | 'any';
  index: number;
  market: PrimaryCountryISOElseGlobal;
  hideIndustryCol?: boolean;
  shortTermReturn: ReactNode;
}

export const Tile = ({
  market,
  index,
  shortTermReturn,
  company: {
    canonicalUrl,
    companyId,
    shortName,
    snowflakePoints,
    tickerSymbol,
    uniqueSymbol,
    return1YrAbs,
    return1YrAbsFormatted,
    marketCap,
    marketCapFormatted,
    peFormatted,
    psFormatted,
    pbFormatted,
    sharePrice,
    sharePriceFormatted,
    bestValuationRatio,
    analystCount,
    earningsGrowth,
    earningsGrowthFormatted,
    salesGrowth,
    salesGrowthFormatted,
    analystsPriceTarget,
    analystsPriceTargetFormatted,
    industrySlug,
    industryLabel,
  },
  section,
}: Props) => {
  const { trackEvent } = useTracking();
  const [visibility, setVisibility] = useState<Visibility>(Visibility.HIDDEN);
  const muteEvent = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  const theme = useTheme();
  const locale = useLocale();

  return (
    <Box position="relative">
      <RouterLink
        to={toLocaleUrl(locale, canonicalUrl)}
        noUnderline
        inheritColor
        onClick={() =>
          trackEvent({
            action: 'click',
            subject: `${section}_next_steps_company`,
            companyId,
            index,
          })
        }
        data-cy-id="link"
      >
        <Observer
          threshold={[0, 1]}
          onChange={({ isIntersecting }) => {
            setVisibility(
              isIntersecting ? Visibility.VISIBLE : Visibility.HIDDEN
            );
          }}
        >
          <Container>
            <SnowflakeWrapper visible={visibility}>
              <Snowflake points={snowflakePoints} />
              <Media
                query={{ maxWidth: theme.xsMaxRaw }}
                render={() => (
                  <Box
                    position="absolute"
                    bottom="17px"
                    left="50%"
                    transform="translateX(-50%)"
                    width="100px"
                  >
                    <Text
                      typography="small"
                      textAlign="center"
                      data-cy-id="company-name"
                      lineClamp={2}
                    >
                      {shortName}
                    </Text>
                  </Box>
                )}
              />
            </SnowflakeWrapper>

            <TileTable>
              <colgroup>
                <col width="50%" />
              </colgroup>
              <Media
                query={{ minWidth: theme.smMinRaw }}
                render={() => (
                  <thead>
                    <tr>
                      <th colSpan={3}>
                        <span>
                          <span>{tickerSymbol}</span>
                          <CellLink
                            to={{
                              type: ROUTE_STOCKS_MARKET_INDUSTRY,
                              payload: {
                                market: market,
                                industry: industrySlug,
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation(); // prevent doubling up on the tracking event on the <tr/>
                              trackEvent({
                                action: 'click',
                                subject: 'table-row-industry-column',
                                uniqueSymbol: uniqueSymbol,
                                companyId: companyId,
                                index: index,
                              });
                            }}
                          >
                            <span>{industryLabel}</span>
                          </CellLink>
                        </span>
                        <CompanyName data-cy-id="company-name">
                          {shortName}
                        </CompanyName>
                      </th>
                    </tr>
                  </thead>
                )}
              />

              <tbody>
                <tr>
                  <td>
                    <span>Price</span>
                    <Value value={sharePrice}>{sharePriceFormatted}</Value>
                  </td>
                  <td>{shortTermReturn}</td>
                  <td>
                    <span>1Y</span>
                    <PNV as="span" value={return1YrAbs}>
                      {return1YrAbsFormatted}
                    </PNV>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>Mkt Cap</span>
                    <Value value={marketCap} opacity={0.7}>
                      {marketCapFormatted}
                    </Value>
                  </td>
                  <td>
                    {!!bestValuationRatio.valuation && (
                      <Tooltip
                        key={`table-tooltip-${companyId}`}
                        title={
                          <ValuationRatio
                            priceToEarnings={peFormatted}
                            priceToBook={pbFormatted}
                            priceToSales={psFormatted}
                          />
                        }
                        light
                        zIndex={99999}
                      >
                        <PELabel onTouchEnd={muteEvent} onClick={muteEvent}>
                          {bestValuationRatio.valuationType}
                        </PELabel>
                      </Tooltip>
                    )}
                    <Value value={bestValuationRatio.valuation} opacity={0.7}>
                      {bestValuationRatio.valuationFormatted}
                    </Value>
                  </td>
                  <td>
                    {earningsGrowth || salesGrowth ? (
                      <Tooltip
                        key={`table-tooltip-${companyId}`}
                        title={
                          <GrowthTooltip
                            analystCount={analystCount}
                            earningsGrowth={earningsGrowthFormatted}
                            salesGrowth={salesGrowthFormatted}
                          />
                        }
                        light
                        zIndex={99999}
                      >
                        <PELabel onTouchEnd={muteEvent} onClick={muteEvent}>
                          {earningsGrowth ? 'E.Growth' : 'S.Growth'}
                        </PELabel>
                      </Tooltip>
                    ) : (
                      <span>Growth</span>
                    )}
                    <Value value={earningsGrowth || salesGrowth} opacity={0.7}>
                      {earningsGrowth
                        ? earningsGrowthFormatted
                        : salesGrowthFormatted}
                    </Value>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span>Analysts Target</span>
                    <Value value={analystsPriceTarget}>
                      {analystsPriceTargetFormatted}
                    </Value>
                  </td>
                  <td>&nbsp;</td>
                  <td />
                </tr>
              </tbody>
            </TileTable>
          </Container>
        </Observer>
      </RouterLink>
      <Actions>
        <WatchlistAddRemoveCompanyToggle
          companyId={companyId}
          tickerSymbol={tickerSymbol}
          styleType="gentle"
          minimal
        />
      </Actions>
    </Box>
  );
};
